import { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { auth } from '../../firebase';
import { useTheme } from '../../context/ThemeContext';
import { FiLock, FiSmartphone, FiUsers, FiDownload, FiBarChart2, FiClock, FiRefreshCw } from 'react-icons/fi';

const AnalyticsPanel = ({
  appPlatformData,
  COLORS,
  formatNumber,
  appsList
}) => {
  const [metrics, setMetrics] = useState({
    activeUsers: 0,
    totalDownloads: 0,
    retentionRate: 0,
    avgSessionTime: 0,
    conversionRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedAppId, setSelectedAppId] = useState('');
  const [allApps, setAllApps] = useState([]);
  const [appLoading, setAppLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [debugInfo, setDebugInfo] = useState('');
  const { isDarkMode } = useTheme();

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const total = appPlatformData.reduce((sum, item) => sum + item.value, 0);
      const percentage = ((payload[0].value / total) * 100).toFixed(1);
      return (
        <div className={`p-3 border rounded-md shadow-md ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
          <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>{payload[0].name}</p>
          <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{formatNumber(payload[0].value)} ({percentage}%)</p>
        </div>
      );
    }
    return null;
  };

  // Formatar tempo de sessão para exibição mais amigável
  const formatSessionTime = (minutes) => {
    if (minutes < 1) return `${Math.round(minutes * 60)} seg`;
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = Math.round(minutes % 60);
      return `${hours}h ${mins}min`;
    }
    return `${Math.round(minutes)} min`;
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const carregarApps = async () => {
      if (!currentUser) return;

      setAppLoading(true);
      try {
        if (appsList && appsList.length > 0) {
          const userApps = appsList.filter(app => app.userId === currentUser.uid);
          setAllApps(userApps);
          if (userApps.length > 0 && !selectedAppId) {
            setSelectedAppId(userApps[0].id);
          }
        } else {
          const q = query(collection(db, 'apps'), where('userId', '==', currentUser.uid));
          const querySnapshot = await getDocs(q);
          const userApps = [];
          querySnapshot.forEach((doc) => {
            userApps.push({ id: doc.id, ...doc.data() });
          });
          setAllApps(userApps);
          if (userApps.length > 0 && !selectedAppId) {
            setSelectedAppId(userApps[0].id);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar apps:', error);
        setDebugInfo('Erro ao carregar aplicativos: ' + error.message);
      } finally {
        setAppLoading(false);
      }
    };
    carregarApps();
  }, [appsList, currentUser, selectedAppId]);

  useEffect(() => {
    const carregarMetricas = async () => {
      if (!selectedAppId || !currentUser) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setDebugInfo(`Buscando métricas para appId: ${selectedAppId}`);

      try {
        const appDoc = await getDoc(doc(db, 'apps', selectedAppId));

        if (!appDoc.exists()) {
          setDebugInfo('Aplicativo não encontrado no Firestore');
          setLoading(false);
          return;
        }

        const appData = appDoc.data();

        if (appData.userId !== currentUser.uid) {
          setDebugInfo('Usuário não tem permissão para acessar este aplicativo');
          setLoading(false);
          return;
        }

        // BUSCA PRINCIPAL - Coleção appMetrics
        const metricsDoc = await getDoc(doc(db, 'appMetrics', selectedAppId));

        if (metricsDoc.exists()) {
          const metricsData = metricsDoc.data();

          const metricasFormatadas = {
            activeUsers: metricsData.activeUsers || metricsData.users || metricsData.active_users || 0,
            totalDownloads: metricsData.totalDownloads || metricsData.downloads || metricsData.total_downloads || 0,
            retentionRate: metricsData.retentionRate || metricsData.retention || metricsData.retention_rate || 0,
            avgSessionTime: metricsData.avgSessionTime || metricsData.sessionTime || metricsData.avg_session_time || 0,
            conversionRate: metricsData.conversionRate || metricsData.conversion || metricsData.conversion_rate || 0
          };

          setMetrics(metricasFormatadas);
          setDebugInfo('As métricas foram carregadas com sucesso.🥳');

        } else {
          // Tenta buscar em subcoleção como fallback
          try {
            const subMetricsDoc = await getDoc(doc(db, 'apps', selectedAppId, 'metrics', 'main'));

            if (subMetricsDoc.exists()) {
              const subMetricsData = subMetricsDoc.data();

              const metricasFormatadas = {
                activeUsers: subMetricsData.activeUsers || subMetricsData.users || 0,
                totalDownloads: subMetricsData.totalDownloads || subMetricsData.downloads || 0,
                retentionRate: subMetricsData.retentionRate || subMetricsData.retention || 0,
                avgSessionTime: subMetricsData.avgSessionTime || subMetricsData.sessionTime || 0,
                conversionRate: subMetricsData.conversionRate || subMetricsData.conversion || 0
              };

              setMetrics(metricasFormatadas);
              setDebugInfo('Métricas carregadas da subcoleção metrics');
            } else {
              setDebugInfo('Nenhum dado de métricas encontrado para este aplicativo');
              setMetrics({
                activeUsers: 0,
                totalDownloads: 0,
                retentionRate: 0,
                avgSessionTime: 0,
                conversionRate: 0
              });
            }
          } catch (subError) {
            setMetrics({
              activeUsers: 0,
              totalDownloads: 0,
              retentionRate: 0,
              avgSessionTime: 0,
              conversionRate: 0
            });
          }
        }

      } catch (error) {
        console.error('Erro ao carregar métricas:', error);
        setDebugInfo('Erro ao carregar métricas: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    if (selectedAppId && currentUser) {
      carregarMetricas();
    }
  }, [selectedAppId, currentUser]);

  const { activeUsers, totalDownloads, retentionRate, avgSessionTime, conversionRate } = metrics;

  const renderGraficoComDados = (data, children, mensagem = "Nenhum dado disponível") => {
    if (!data || data.length === 0) {
      return (
        <div className={`h-64 flex items-center justify-center rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{mensagem}</p>
        </div>
      );
    }
    return children;
  };

  if (!currentUser) {
    return (
      <div className="flex justify-center items-center h-64">
          <div className="text-center">
          <FiLock className="text-gray-400 text-6xl mb-4" />
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Acesso não autorizado</h3>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Faça login para visualizar as métricas</p>
        </div>
      </div>
    );
  }

  if (appLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className={`ml-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Carregando aplicativos...</span>
      </div>
    );
  }

  if (allApps.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
          <div className="text-center">
          <FiSmartphone className="text-gray-400 text-6xl mb-4" />
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Nenhum aplicativo encontrado</h3>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Você ainda não possui aplicativos cadastrados</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {debugInfo && (
        <div className={`p-3 rounded-md border ${isDarkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'}`}>
          <p className={`text-sm ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>{debugInfo}</p>
        </div>
      )}

      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} px-4 py-3.5 sm:px-5 sm:py-4 rounded-xl shadow-sm border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3`}>
        <h3 className={`text-sm sm:text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} flex items-center gap-2`}>
            <FiSmartphone className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} /> Selecionar Aplicativo
        </h3>
        <select
          value={selectedAppId}
          onChange={(e) => setSelectedAppId(e.target.value)}
          className={`w-full sm:w-auto min-w-[250px] p-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900 focus:bg-white'}`}
        >
          {allApps.map((app) => (
            <option key={app.id} value={app.id}>{app.name} - {app.userEmail}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className={`ml-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Carregando métricas...</span>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {/* Usuários Ativos */}
            <div className={`relative overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} p-4 rounded-xl shadow-sm border group`}>
              <div className="flex justify-between items-start mb-2">
                <h3 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Usuários Ativos</h3>
                <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600'} transition-transform group-hover:scale-110`}>
                  <FiUsers className="w-4 h-4" />
                </div>
              </div>
              <p className={`text-2xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatNumber(activeUsers)}</p>
              <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Total de usuários no app</p>
            </div>

            {/* Downloads Totais */}
            <div className={`relative overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} p-4 rounded-xl shadow-sm border group`}>
              <div className="flex justify-between items-start mb-2">
                <h3 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Downloads</h3>
                <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-600'} transition-transform group-hover:scale-110`}>
                  <FiDownload className="w-4 h-4" />
                </div>
              </div>
              <p className={`text-2xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatNumber(totalDownloads)}</p>
              <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Realizados até hoje</p>
            </div>

            {/* Taxa de Retenção */}
            <div className={`relative overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} p-4 rounded-xl shadow-sm border group`}>
              <div className="flex justify-between items-start mb-2">
                <h3 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Retenção</h3>
                <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-50 text-purple-600'} transition-transform group-hover:scale-110`}>
                  <FiBarChart2 className="w-4 h-4" />
                </div>
              </div>
              <p className={`text-2xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{retentionRate}%</p>
              <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Percentual retido</p>
            </div>

            {/* Tempo Médio de Sessão */}
            <div className={`relative overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} p-4 rounded-xl shadow-sm border group`}>
              <div className="flex justify-between items-start mb-2">
                <h3 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tempo Médio</h3>
                <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-50 text-orange-600'} transition-transform group-hover:scale-110`}>
                  <FiClock className="w-4 h-4" />
                </div>
              </div>
              <p className={`text-2xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatSessionTime(avgSessionTime)}</p>
              <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Média por sessão</p>
            </div>

            {/* Taxa de Conversão */}
            <div className={`relative overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} p-4 rounded-xl shadow-sm border group md:col-span-1 lg:col-span-1`}>
              <div className="flex justify-between items-start mb-2">
                <h3 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Conversão</h3>
                <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-teal-900/30 text-teal-400' : 'bg-teal-50 text-teal-600'} transition-transform group-hover:scale-110`}>
                  <FiRefreshCw className="w-4 h-4" />
                </div>
              </div>
              <p className={`text-2xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{conversionRate}%</p>
              <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Taxa de conversão</p>
            </div>
          </div>

          {/* Gráfico de Distribuição por Plataforma */}
          <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} p-5 sm:p-6 rounded-xl shadow-sm border`}>
            <div className="flex items-center justify-between mb-4">
                <h3 className={`text-sm sm:text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Distribuição por Plataforma</h3>
            </div>
            {renderGraficoComDados(appPlatformData, (
              <div className="h-56 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={appPlatformData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percent }) => (
                        <text 
                          fill={isDarkMode ? '#f3f4f6' : '#374151'} 
                          fontSize="11px"
                          fontWeight="500"
                        >
                          {name}: {(percent * 100).toFixed(0)}%
                        </text>
                      )}
                      outerRadius={75}
                      innerRadius={55}
                      fill="#8884d8"
                      dataKey="value"
                      paddingAngle={3}
                    >
                      {appPlatformData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                    <Legend
                      layout="horizontal"
                      verticalAlign="bottom"
                      align="center"
                      iconType="circle"
                      formatter={(value, entry, index) => (
                        <span className={`text-xs font-medium ml-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {value}: {appPlatformData[index]?.value}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ), "Nenhum dado de plataformas disponível no momento.")}
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsPanel;